import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { ExtendedLink } from '@/components/ExtendedLink';
import {
  type DetailedBagSeller,
  type DetailedBagSellerInfo,
} from '@/modules/bag/aliases';
import { transformBagProductToCheckoutProduct } from '@/modules/bag/helpers';
import { type BagTrackingTypes } from '@/modules/bag/types';
import { useBagTracking } from '@/modules/bag/useBagTracking';
import {
  CHECKOUT_LANDING_PATH,
  CHECKOUT_QUERY_PARAM,
} from '@/modules/checkout/constants';
import { addCheckoutProducts } from '@/modules/checkout/helpers';
import { guestCheckoutSummaryQueryOptions } from '@/modules/checkout/queries';
import { CheckoutType, type CheckoutProduct } from '@/modules/checkout/types';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config';
import { clearCombinedFlowCookie } from '@/modules/experiments/helpers/clearCombinedFlowCookie';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { useCurrentLanguage } from '@/modules/language/useCurrentLanguage';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  products: DetailedBagSeller['products'];
  seller: DetailedBagSellerInfo;
  trackingType: BagTrackingTypes;
};

function BagCheckoutButton({ products, seller, trackingType }: Props) {
  const t = useTranslations('bag');
  const bagTracking = useBagTracking();
  const [isAuthenticated] = useCurrentUser();
  const { language } = useCurrentLanguage();
  const queryClient = useQueryClient();

  const [{ web_8651_buy_now_presignup }, bucketFn] = useExperiments(
    ['web_8651_buy_now_presignup'],
    { deferred: true }
  );

  const isBuyNowPresignupVariant =
    web_8651_buy_now_presignup === VARIANT_IDENTIFIER;
  const loginParams = new URLSearchParams({
    redirect: CHECKOUT_LANDING_PATH,
    [CHECKOUT_QUERY_PARAM]: 'true',
  });
  if (isBuyNowPresignupVariant) {
    loginParams.set('buyNow', 'true');
  }
  const checkoutHref = isAuthenticated
    ? '/pay/'
    : `/login/?${loginParams.toString()}`;

  function handleClick() {
    const transformedProducts: Array<CheckoutProduct> = [];
    products.forEach((product) => {
      const quantity = product.quantity || 1;
      for (let i = 0; i < quantity; i++) {
        transformedProducts.push(
          transformBagProductToCheckoutProduct(product, seller)
        );
      }
    });
    addCheckoutProducts({
      type: CheckoutType.Bag,
      products: transformedProducts,
    });

    bagTracking.sendBagCheckoutAction(trackingType);

    if (!isAuthenticated) {
      bucketFn();
      void queryClient.prefetchQuery(
        guestCheckoutSummaryQueryOptions({
          products: transformedProducts,
          language,
        })
      );
    } else {
      void clearCombinedFlowCookie();
    }
  }

  return (
    <ExtendedLink href={checkoutHref} onClick={handleClick}>
      <ButtonExperiment as="span" fullWidth>
        {t('Bag.Summary.CheckoutButton', { count: products.length })}
      </ButtonExperiment>
    </ExtendedLink>
  );
}

export { BagCheckoutButton };
